import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, Star, Loader2 } from "lucide-react";
import { getImageUri } from "../core/utils/imageLoad";
import { useTopRatedProviders } from "../hooks/control"; // Ajusta la ruta a tu archivo de hooks
import { useAuthSessionContext } from "./routing/auth-session-store";
import type { ProviderResponse } from "../core/types/control"; // Ajusta la ruta a tus tipos

// Definimos el tipo de respuesta del backend para este endpoint específico
type TopRatedResponse = {
	success: boolean;
	data: ProviderResponse[];
};

export function CarouselTopProviders() {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [isPaused, setIsPaused] = useState(false);
	const [viewportWidth, setViewportWidth] = useState<number>(() => window.innerWidth);
	const autoScrollInterval = useRef<ReturnType<typeof setInterval> | null>(null);
	const { session } = useAuthSessionContext();
	const canViewProfile = Boolean(session);

	// 1. Usamos el hook específico.
	// Nota: Aunque el hook esté tipado como Provider[], la API devuelve un objeto { data: Provider[] }
	// Usamos 'unknown' y cast o una interfaz intermedia para manejar la respuesta real del JSON.
	const { data: apiResponse, loading, error } = useTopRatedProviders();

	// 2. Extraemos el array de proveedores de la respuesta del backend
	const providers = useMemo(() => {
		if (!apiResponse) return [];

		// Si la respuesta es el array directo (por si cambia el backend)
		if (Array.isArray(apiResponse)) return apiResponse;

		// Si la respuesta es el objeto { success, data: [...] } como muestra tu JSON
		const response = apiResponse as unknown as TopRatedResponse;
		return response.data || [];
	}, [apiResponse]);

	const getCardWidth = () => {
		if (!scrollRef.current) return 0;
		const firstCard = scrollRef.current.firstElementChild as HTMLElement | null;
		if (!firstCard) return 0;
		const styles = window.getComputedStyle(scrollRef.current);
		const gap = Number.parseFloat(styles.columnGap || "0") || 0;
		return firstCard.offsetWidth + gap;
	};

	useEffect(() => {
		const onResize = () => {
			setViewportWidth(window.innerWidth);
		};

		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const isMobile = viewportWidth < 640;

	const scrollOne = useCallback(
		(direction: "left" | "right") => {
			if (scrollRef.current && providers.length > 0) {
				const cardWidth = getCardWidth();
				const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

				let scrollTo =
					direction === "left"
						? scrollLeft - cardWidth
						: scrollLeft + cardWidth;

				if (
					direction === "right" &&
					scrollLeft + clientWidth >= scrollWidth - 10
				) {
					scrollTo = 0;
				} else if (direction === "left" && scrollLeft <= 0) {
					scrollTo = scrollWidth - clientWidth;
				}

				scrollRef.current.scrollTo({
					left: scrollTo,
					behavior: "smooth",
				});
			}
		},
		[providers.length],
	);

	useEffect(() => {
		if (isPaused || providers.length === 0 || isMobile) {
			if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
			return;
		}

		autoScrollInterval.current = setInterval(() => {
			scrollOne("right");
		}, 3000);

		return () => {
			if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
		};
	}, [isPaused, scrollOne, providers.length, isMobile]);

	// --- RENDERIZADO ---

	if (loading) {
		return (
			<div className='mx-auto w-full max-w-7xl px-4 py-16 flex flex-col items-center justify-center text-gray-500'>
				<Loader2 className='h-8 w-8 animate-spin mb-2' />
				<span>Cargando proveedores populares...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className='mx-auto w-full max-w-7xl px-4 py-16 text-center text-red-500'>
				Error al cargar proveedores. Intente nuevamente.
			</div>
		);
	}

	if (providers.length === 0) {
		return null;
	}

	return (
		<div className='mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 sm:py-14 md:py-20 lg:px-8'>
			<div className='mb-4 flex w-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-end'>
				<div className='text-xl font-semibold leading-7 text-neutral-900 sm:text-2xl'>
					Proveedores más populares
				</div>
				<p className='text-sm text-neutral-500 sm:text-base'>
					Explora perfiles destacados y elige con confianza.
				</p>
			</div>

			<div
				className='group relative mt-6'
				onMouseEnter={() => setIsPaused(true)}
				onMouseLeave={() => setIsPaused(false)}
				onTouchStart={() => setIsPaused(true)}
				onTouchEnd={() => setIsPaused(false)}
			>
				<button
					onClick={() => scrollOne("left")}
					className='absolute left-0 top-1/2 z-30 -translate-y-1/2 rounded-full bg-white/80 p-2 shadow-md transition-all hover:bg-white opacity-0 group-hover:opacity-100 hidden md:block cursor-pointer'
					aria-label='Anterior'
				>
					<ChevronLeft className='w-6 h-6 text-gray-600' />
				</button>

				<div
					ref={scrollRef}
					className='flex gap-3 overflow-x-auto snap-x snap-mandatory snap-always overscroll-x-contain pb-4 pl-2 pr-2 sm:gap-6 sm:pl-0 sm:pr-0 md:px-1 [&::-webkit-scrollbar]:hidden'
					style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
				>
					{providers.map((provider) => {
						// AHORA: Accedemos directamente a las propiedades, NO usamos .attributes

						// Construcción segura de la URL de la imagen
						const userImage = provider.users?.image_profile;
						const fallbackImageUrl = "";

						// Si no hay imagen del usuario, usamos fallback para evitar src vacío
						const imageUrl = getImageUri(userImage) ?? fallbackImageUrl;

						// Obtener categorias del proveedor
						const activeServices = Array.isArray(provider.services_providers)
							? provider.services_providers.filter(
									(service) => service?.is_active !== false,
								)
							: [];

						const categoryNamesFromProvider = Array.isArray(
							provider.categories_providers,
						)
							? provider.categories_providers
									.map((category) =>
										category?.categories_services?.name_category?.trim(),
									)
									.filter(
										(name): name is string =>
											typeof name === "string" && name.length > 0,
									)
							: [];

						const categoryNamesFromServices = activeServices.flatMap(
							(service) =>
								Array.isArray(service.categories_services_providers)
									? service.categories_services_providers
											.map((item) =>
												item?.categories_providers?.categories_services?.name_category?.trim(),
											)
											.filter(
												(name): name is string =>
													typeof name === "string" &&
													name.length > 0,
											)
									: [],
						);

						const uniqueCategories = [
							...new Set([
								...categoryNamesFromProvider,
								...categoryNamesFromServices,
							]),
						];

						const categoriesLabel =
							uniqueCategories.join(", ") || "Sin categoria";

						const mobileCategoriesLabel =
							uniqueCategories.length > 2
								? `${uniqueCategories.slice(0, 2).join(", ")} +${uniqueCategories.length - 2}`
								: categoriesLabel;

						return (
							<div
								key={provider.id}
								className='w-[78vw] max-w-75 shrink-0 snap-center overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm sm:w-70 sm:snap-start sm:rounded-2xl'
							>
								<div className='flex h-full flex-col'>
									<div className='relative h-52 w-full overflow-hidden bg-gray-100 sm:h-48'>
										<img
											src={imageUrl}
											alt={provider.company_name}
											className='h-full w-full object-cover transition-transform duration-300 hover:scale-105'
											onError={(e) => {
												// Imagen por defecto si falla la carga
												(e.target as HTMLImageElement).src =
													fallbackImageUrl;
											}}
										/>
										<div className='pointer-events-none absolute inset-0 bg-linear-to-t from-black/65 via-black/15 to-transparent sm:hidden' />
										<div
											className='absolute left-3 top-3 max-w-[calc(100%-5.25rem)] truncate whitespace-nowrap rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-medium text-primary shadow-sm sm:hidden'
											title={categoriesLabel}
										>
											{mobileCategoriesLabel}
										</div>
										<div className='absolute right-3 top-3 flex items-center rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-primary shadow-sm sm:hidden'>
											<Star className='mr-1 h-3.5 w-3.5 fill-current' />
											{(Number(provider.rating) || 0).toFixed(1)}
										</div>
										<div className='absolute inset-x-3 bottom-3 sm:hidden'>
											<h3
												className='line-clamp-1 text-base font-semibold text-white'
												title={provider.company_name}
											>
												{provider.company_name}
											</h3>
										</div>
									</div>

									<div
										className={
											canViewProfile
												? "flex grow flex-col p-4"
												: "hidden sm:flex sm:grow sm:flex-col sm:p-4"
										}
									>
										<div className='mb-1 flex items-start justify-between gap-2'>
											<h3
												className='hidden line-clamp-1 text-[15px] font-semibold text-gray-800 sm:block sm:text-base'
												title={provider.company_name}
											>
												{provider.company_name}
											</h3>

											<div className='hidden items-center text-xs font-medium text-primary sm:flex sm:text-sm'>
												<Star className='mr-1 h-4 w-4 fill-current' />
												{/* Aseguramos que rating sea un número */}
												{(Number(provider.rating) || 0).toFixed(
													1,
												)}
											</div>
										</div>

										<p className='mb-3 hidden line-clamp-1 text-sm text-gray-500 sm:block'>
											{categoriesLabel}
										</p>

										{canViewProfile ? (
											<div className='mt-auto'>
												<a
													href={`/proveedor/${provider.id}`}
													className='inline-flex w-full items-center justify-center rounded-full border border-primary/30 bg-primary/5 px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 sm:inline-block sm:w-auto sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:text-cyan-600 sm:hover:text-cyan-700'
												>
													Ver perfil
												</a>
											</div>
										) : null}
									</div>
								</div>
							</div>
						);
					})}
				</div>

				<button
					onClick={() => scrollOne("right")}
					className='absolute right-0 top-1/2 z-30 -translate-y-1/2 rounded-full bg-white/80 p-2 shadow-md transition-all hover:bg-white opacity-0 group-hover:opacity-100 hidden md:block cursor-pointer'
					aria-label='Siguiente'
				>
					<ChevronRight className='w-6 h-6 text-gray-600' />
				</button>
			</div>
		</div>
	);
}
